import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  Menu,
  X,
  Phone,
  UserCircle2,
  LogOut,
  ShieldCheck,
  ChevronDown,
  CalendarCheck,
  Briefcase,
  MessageSquare,
  MessageCircle,
  UserRound,
} from "lucide-react";
import { trackSignupEvent } from "@/lib/signup-analytics";
import { useQueryClient } from "@tanstack/react-query";
import { site } from "@/lib/site";
import { cn } from "@/lib/utils";
import logoAsset from "@/assets/logo.jpg.asset.json";
import logo48 from "@/assets/logo-48.webp.asset.json";
import logo96 from "@/assets/logo-96.webp.asset.json";
import logo144 from "@/assets/logo-144.webp.asset.json";
import logo48Avif from "@/assets/logo-48.avif.asset.json";
import logo96Avif from "@/assets/logo-96.avif.asset.json";
import logo144Avif from "@/assets/logo-144.avif.asset.json";
import { ComplianceBar } from "./ComplianceBar";

import { supabase } from "@/integrations/supabase/client";

// Primary nav — always visible on desktop
const primaryNav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/cv-builder", label: "Free CV Builder", highlight: true },
  { to: "/legislation", label: "Legislation" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
] as const;


// Grouped "More" dropdown
const moreNav: { heading: string; items: { to: string; label: string }[] }[] = [
  {
    heading: "Education",
    items: [
      { to: "/education", label: "Education Division" },
      { to: "/resources", label: "Resource Library" },
      { to: "/tools", label: "Free Tools" },
    ],
  },
  {
    heading: "Media",
    items: [
      { to: "/radio", label: "Radio" },
      { to: "/daily-legal", label: "Daily Legal" },
    ],
  },
  {
    heading: "Learn more",
    items: [
      { to: "/guides", label: "Legal Guides" },
      { to: "/contracts", label: "Contract Templates" },
    ],
  },
  {
    heading: "Proof & trust",
    items: [
      { to: "/case-studies", label: "Case Studies" },
      { to: "/attorneys", label: "Attorney Partnerships" },
    ],
  },
  {
    heading: "Work with us",
    items: [
      { to: "/document-services", label: "Document Drafting" },
      { to: "/cv-services", label: "CV & Coaching" },

      { to: "/crime-awareness", label: "Awareness Talks" },
      { to: "/careers", label: "Careers" },
    ],
  },
];



export function Header() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const router = useRouter();
  const qc = useQueryClient();
  const moreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setEmail(data.session?.user.email ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED")
        return;
      setEmail(session?.user.email ?? null);
      router.invalidate();
      if (event !== "SIGNED_OUT") qc.invalidateQueries();
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [router, qc]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!moreRef.current) return;
      if (!moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    navigate({ to: "/" });
  }

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <ComplianceBar />
      <div className="container-narrow grid h-16 grid-cols-[minmax(0,1fr)_auto] items-center gap-4 lg:flex lg:justify-between">
        {/* Brand */}
        <Link to="/" className="flex min-w-0 items-center gap-3 group">
          <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-md bg-navy shadow-soft">
            <picture>
              <source
                type="image/avif"
                srcSet={`${logo48Avif.url} 1x, ${logo96Avif.url} 2x, ${logo144Avif.url} 3x`}
              />
              <source
                type="image/webp"
                srcSet={`${logo48.url} 1x, ${logo96.url} 2x, ${logo144.url} 3x`}
              />
              <img
                src={logoAsset.url}
                alt="Nkadimeng Danny Legal & Compliance Support logo"
                width={40}
                height={40}
                decoding="async"
                fetchPriority="high"
                className="h-full w-full object-cover"
              />
            </picture>
          </span>
          <span className="hidden sm:flex min-w-0 flex-col leading-tight">
            <span className="truncate font-display text-base font-semibold text-navy">
              Nkadimeng Danny
            </span>
            <span className="truncate text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Legal &amp; Compliance Support
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {primaryNav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={cn(
                "whitespace-nowrap text-sm font-medium transition-colors",
                (n as any).highlight
                  ? "rounded-full bg-gold/15 px-3 py-1 text-navy hover:bg-gold/25"
                  : "text-foreground/80 hover:text-navy",
              )}
              activeProps={{ className: "text-navy" }}
              activeOptions={{ exact: n.to === "/" }}
            >
              {n.label}
            </Link>
          ))}
          <div className="relative" ref={moreRef}>
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-navy"
              aria-expanded={moreOpen}
              aria-haspopup="menu"
            >
              More
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  moreOpen && "rotate-180",
                )}
              />
            </button>
            {moreOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full z-50 mt-3 w-72 overflow-hidden rounded-lg border border-border bg-card p-3 shadow-lifted"
              >
                <div className="grid gap-3">
                  {moreNav.map((group) => (
                    <div key={group.heading}>
                      <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-gold">
                        {group.heading}
                      </p>
                      <ul className="grid">
                        {group.items.map((it) => (
                          <li key={it.to}>
                            <Link
                              to={it.to}
                              onClick={() => setMoreOpen(false)}
                              className="block rounded-md px-2 py-1.5 text-sm text-foreground/85 hover:bg-secondary hover:text-navy"
                            >
                              {it.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Desktop right-side actions */}
        <div className="hidden lg:flex shrink-0 items-center gap-2 xl:gap-3">
          <a
            href={`tel:${site.phoneIntl}`}
            className="hidden xl:inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-navy"
          >
            <Phone className="h-4 w-4" />
            {site.phone}
          </a>
          {email ? (
            <>
              <Link
                to={"/portal" as any}
                className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm hover:border-navy"
              >
                <UserCircle2 className="h-4 w-4" /> Portal
              </Link>
              <button
                onClick={signOut}
                aria-label="Sign out"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input text-foreground/70 hover:text-navy"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link
                to={"/auth" as any}
                search={{ role: "client" } as any}
                className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm hover:border-navy"
              >
                <UserCircle2 className="h-4 w-4" /> Sign in
              </Link>
              <Link
                to={"/auth" as any}
                search={{ mode: "signup" } as any}
                onClick={() => trackSignupEvent("signup_click", "header")}
                className="inline-flex items-center gap-1.5 rounded-md bg-gold px-3 py-2 text-sm font-semibold text-gold-foreground shadow-gold hover:brightness-95"
              >
                <UserRound className="h-4 w-4" /> Sign up free
              </Link>
              <Link
                to={"/auth" as any}
                search={{ role: "admin" } as any}
                title="Admin sign-in"
                aria-label="Admin sign-in"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-navy/40 bg-navy/5 text-navy hover:bg-navy hover:text-navy-foreground"
              >
                <ShieldCheck className="h-4 w-4" />
              </Link>
            </>
          )}
          
          <Link
            to="/book"
            className="inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground shadow-soft hover:bg-navy/90 transition-colors"
          >
            Book
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          aria-label="Toggle menu"
          className="lg:hidden inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border text-navy"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          "lg:hidden border-t border-border/60 bg-background",
          open ? "max-h-[calc(100vh-7rem)] overflow-y-auto" : "max-h-0 overflow-hidden",
          "transition-[max-height] duration-300 ease-out",
        )}
      >
        <div className="container-narrow flex flex-col py-4">
          {/* Quick-action chips */}
          <div className="grid grid-cols-3 gap-2">
            <Link
              to="/book"
              onClick={() => setOpen(false)}
              className="flex flex-col items-center gap-1 rounded-lg bg-navy px-2 py-3 text-[11px] font-semibold text-navy-foreground"
            >
              <CalendarCheck className="h-4 w-4 text-gold" />
              Book
            </Link>
            <Link
              to="/services"
              onClick={() => setOpen(false)}
              className="flex flex-col items-center gap-1 rounded-lg border border-gold/40 bg-gold/10 px-2 py-3 text-[11px] font-semibold text-navy"
            >
              <Briefcase className="h-4 w-4 text-gold" />
              Services
            </Link>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="flex flex-col items-center gap-1 rounded-lg border border-border bg-card px-2 py-3 text-[11px] font-semibold text-navy"
            >
              <MessageSquare className="h-4 w-4 text-navy" />
              Contact
            </Link>
          </div>

          {/* Quick contact row */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <a
              href={`tel:${site.phoneIntl}`}
              className="flex items-center justify-center gap-2 rounded-md border border-border bg-background py-2.5 text-xs font-medium text-navy"
            >
              <Phone className="h-3.5 w-3.5 text-gold" /> Call
            </a>
            <a
              href={`https://wa.me/${site.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-md bg-[#25D366] py-2.5 text-xs font-medium text-white"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </a>
          </div>

          <div className="mt-4 border-t border-border/60 pt-2">
            {primaryNav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="block py-2.5 text-sm font-medium text-foreground/85"
                activeProps={{ className: "text-navy" }}
                activeOptions={{ exact: n.to === "/" }}
              >
                {n.label}
              </Link>
            ))}
          </div>

          {/* More — grouped */}
          <div className="mt-3 border-t border-border/60 pt-3">
            <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-gold">
              More
            </p>
            {moreNav.map((group) => (
              <div key={group.heading} className="mt-2">
                <p className="px-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                  {group.heading}
                </p>
                <ul className="grid">
                  {group.items.map((it) => (
                    <li key={it.to}>
                      <Link
                        to={it.to}
                        onClick={() => setOpen(false)}
                        className="block py-2 text-sm text-foreground/85 hover:text-navy"
                      >
                        {it.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-4 border-t border-border/60 pt-3">
            {email ? (
              <Link
                to={"/portal" as any}
                onClick={() => setOpen(false)}
                className="block py-2.5 text-sm font-semibold text-navy"
              >
                Client Portal
              </Link>
            ) : (
              <div className="flex flex-col gap-2">
                <Link
                  to={"/auth" as any}
                  search={{ mode: "signup" } as any}
                  onClick={() => {
                    trackSignupEvent("signup_click", "header_mobile");
                    setOpen(false);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-gold px-3 py-2.5 text-sm font-semibold text-gold-foreground shadow-gold"
                >
                  <UserRound className="h-4 w-4" /> Sign up free
                </Link>
                <Link
                  to={"/auth" as any}
                  onClick={() => setOpen(false)}
                  className="block py-2 text-sm font-medium text-navy"
                >
                  Already have an account? Sign in
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
